import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { eventsApi } from '../services/api';
import { Calendar, MapPin, Ticket } from 'lucide-react';

interface EventItem {
  id: string;
  name: string;
  date: string;
  endDate?: string;
  location?: string;
  description?: string;
  coverImage?: string;
  _count?: { photos?: number; tickets?: number };
  business?: { name?: string };
}

const Events: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadEvents uses page from closure
  }, [page]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await eventsApi.getUpcoming(page);
      if (res?.success && Array.isArray(res?.data)) {
        setEvents((prev) => (page === 1 ? res.data : [...prev, ...res.data]));
        setHasMore(res?.pagination ? res.pagination.page < res.pagination.pages : false);
      } else {
        setError(res?.error || 'Failed to load events');
      }
    } catch (err) {
      console.error('Load events failed:', err);
      setError('Failed to load events. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 25, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>🎉 Events</h1>
          <p style={{ margin: '10px 0 0 0', color: '#666' }}>Discover and attend events</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => navigate('/tickets')}
            style={{
              padding: '12px 20px',
              background: 'rgba(102, 126, 234, 0.15)',
              color: '#667eea',
              border: '1px solid #667eea',
              borderRadius: 10,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            My Tickets
          </button>
          <button
            onClick={() => navigate('/events/create')}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: 10,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            + Create Event
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: 16, background: '#fef2f2', color: '#b91c1c', borderRadius: 10, marginBottom: 20 }}>
          {error}
        </div>
      )}

      {loading && events.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 32 }}>⏳</div>
          <p style={{ color: '#666', marginTop: 12 }}>Loading events...</p>
        </div>
      ) : events.length === 0 ? (
        <div style={{ background: 'white', padding: 50, borderRadius: 12, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <span style={{ fontSize: 48 }}>🎉</span>
          <p style={{ color: '#666', marginTop: 15 }}>No events at the moment</p>
          <p style={{ color: '#999', fontSize: 14 }}>Check back soon or create an event in a Group</p>
          <button
            onClick={() => navigate('/groups')}
            style={{
              marginTop: 16,
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Go to Groups
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {events.map((ev) => (
            <div
              key={ev.id}
              style={{
                background: 'white',
                borderRadius: 12,
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                display: 'flex',
                flexDirection: 'row',
                minHeight: 120,
              }}
            >
              <div
                style={{
                  width: 140,
                  minHeight: 120,
                  background: ev.coverImage ? `url(${ev.coverImage}) center/cover` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: 32,
                }}
              >
                {!ev.coverImage && '🎫'}
              </div>
              <div style={{ flex: 1, padding: 20, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: 18 }}>{ev.name}</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, color: '#666', fontSize: 14 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Calendar size={14} />
                    {formatDate(ev.date)}
                  </span>
                  {ev.location && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <MapPin size={14} />
                      {ev.location}
                    </span>
                  )}
                  {(ev._count?.tickets ?? 0) > 0 && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Ticket size={14} />
                      Tickets available
                    </span>
                  )}
                </div>
                {ev.business?.name && (
                  <p style={{ margin: '8px 0 0 0', color: '#999', fontSize: 13 }}>by {ev.business.name}</p>
                )}
              </div>
              <div style={{ padding: 20, display: 'flex', alignItems: 'center' }}>
                <button
                  onClick={() => navigate(`/tickets`)}
                  style={{
                    padding: '8px 16px',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  View tickets
                </button>
              </div>
            </div>
          ))}
          {hasMore && (
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={loading}
              style={{
                padding: 12,
                background: '#f1f5f9',
                border: 'none',
                borderRadius: 10,
                cursor: loading ? 'not-allowed' : 'pointer',
                color: '#64748b',
              }}
            >
              {loading ? 'Loading...' : 'Load more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Events;
